import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { EventDetail } from "src/types/eventDetail";
import apiInstance from "src/api/instance";
import endpoints from "src/api/endpoints";
import ScoreBoard from "./score-board";
import parseNumber from "src/utils/parse-number";
import { formatUnix } from "src/utils/date";
import { BASE_PATH } from "src/config/constants";

interface AnalysisRightBarProps {
  event: EventDetail;
  score?: any;
}

type BetTab = "matched" | "unmatched";
type ViewMoreTab = "matched" | "deleted";

interface MyBetItem {
  bet_id?: string;
  user_name?: string;
  runner_name?: string;
  nation?: string;
  market_name?: string;
  user_rate?: number | string;
  requested_odds?: number | string;
  odds?: number | string;
  bet_amount?: number | string;
  placed_at?: string;
  created_at?: string;
  game_name?: string;
  ip_address?: string;
  bet_type?: string;
  bet_status?: string;
  market_type?: string;
  type?: string;
  betType?: string;
}

const asList = (payload: any): MyBetItem[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.listBet)) return payload.data.listBet;
  if (Array.isArray(payload?.data?.betList)) return payload.data.betList;
  if (Array.isArray(payload?.listBet)) return payload.listBet;
  if (Array.isArray(payload?.betList)) return payload.betList;
  return [];
};

const getBetDate = (bet: MyBetItem) => bet.placed_at || bet.created_at || "";
const getRate = (bet: MyBetItem) => bet.user_rate ?? bet.requested_odds ?? bet.odds ?? "-";
const getNation = (bet: MyBetItem) => bet.nation || bet.runner_name || bet.market_name || "-";
const getAmount = (bet: MyBetItem) => Number(parseNumber({ value: bet.bet_amount, type: "float" })).toFixed(2);
const getBetType = (bet: MyBetItem) => String(bet.type || bet.betType || "").trim().toLowerCase();
const getMatchState = (bet: MyBetItem) => String(bet.bet_type || "").trim().toLowerCase();
const getRowClass = (bet: MyBetItem) => (getBetType(bet) === "lay" ? "lay-border" : "back-border");

const AnalysisRightBar = ({ event, score }: AnalysisRightBarProps) => {
  const [bets, setBets] = useState<MyBetItem[]>([]);
  const [activeBetTab, setActiveBetTab] = useState<BetTab>("matched");
  const [viewMoreOpen, setViewMoreOpen] = useState(false);
  const [viewMoreTab, setViewMoreTab] = useState<ViewMoreTab>("matched");
  const [viewMoreMarketTab, setViewMoreMarketTab] = useState("ALL");
  const [searchUserName, setSearchUserName] = useState("");
  const [searchAmountFrom, setSearchAmountFrom] = useState("");
  const [searchAmountTo, setSearchAmountTo] = useState("");
  const [searchIp, setSearchIp] = useState("");
  const [betTypeFilter, setBetTypeFilter] = useState<"all" | "back" | "lay" | "deleted">("all");

  useEffect(() => {
    let mounted = true;
    apiInstance
      .get(endpoints.bet.betList, {
        params: {
          bet_status: "unsettle",
          event_id: event.game_event_id,
          limit: 100,
          offset: 0,
        },
      })
      .then(({ data }) => {
        if (!mounted) return;
        setBets(asList(data));
      })
      .catch(() => {
        if (!mounted) return;
        setBets([]);
      });

    return () => {
      mounted = false;
    };
  }, [event.game_event_id, event.provider_event_id]);

  const matchedBets = useMemo(
    () =>
      bets.filter((bet) => {
        const matchState = getMatchState(bet);
        return ["matched", "match", "m"].includes(matchState);
      }),
    [bets]
  );

  const unmatchedBets = useMemo(
    () =>
      bets.filter((bet) => {
        const matchState = getMatchState(bet);
        return ["unmatched", "un-match", "un match", "um", "u"].includes(matchState);
      }),
    [bets]
  );

  const deletedBets = useMemo(
    () =>
      bets.filter((bet) => {
        const status = String(bet.bet_status || "").toLowerCase();
        return ["deleted", "voided", "cancelled"].includes(status);
      }),
    [bets]
  );

  const sidebarList = activeBetTab === "matched" ? matchedBets : unmatchedBets;
  const viewMoreBaseList = viewMoreTab === "matched" ? matchedBets : deletedBets;

  const marketTabs = useMemo(() => {
    const tabs = Array.from(
      new Set(viewMoreBaseList.map((bet) => String(bet.market_type || bet.game_name || "Normal").toUpperCase()))
    ).filter(Boolean);
    return tabs.length ? tabs : ["NORMAL"];
  }, [viewMoreBaseList]);

  useEffect(() => {
    if (!viewMoreOpen) return;
    if (!marketTabs.includes(viewMoreMarketTab)) {
      setViewMoreMarketTab(marketTabs[0]);
    }
  }, [marketTabs, viewMoreMarketTab, viewMoreOpen]);

  const viewMoreList = useMemo(() => {
    const filteredByMarket = viewMoreBaseList.filter(
      (bet) => String(bet.market_type || bet.game_name || "Normal").toUpperCase() === viewMoreMarketTab
    );

    const amountFrom = Number(searchAmountFrom);
    const amountTo = Number(searchAmountTo);

    return filteredByMarket.filter((bet) => {
      const user = String(bet.user_name || "").toLowerCase();
      const ip = String(bet.ip_address || "").toLowerCase();
      const amount = Number(parseNumber({ value: bet.bet_amount, type: "float" }));
      const type = getBetType(bet);
      const status = String(bet.bet_status || "").toLowerCase();

      if (searchUserName.trim() && !user.includes(searchUserName.trim().toLowerCase())) return false;
      if (searchIp.trim() && !ip.includes(searchIp.trim().toLowerCase())) return false;
      if (Number.isFinite(amountFrom) && searchAmountFrom !== "" && amount < amountFrom) return false;
      if (Number.isFinite(amountTo) && searchAmountTo !== "" && amount > amountTo) return false;

      if (betTypeFilter === "back" && type !== "back") return false;
      if (betTypeFilter === "lay" && type !== "lay") return false;
      if (betTypeFilter === "deleted" && !["deleted", "voided", "cancelled"].includes(status)) return false;

      return true;
    });
  }, [
    viewMoreBaseList,
    viewMoreMarketTab,
    searchUserName,
    searchIp,
    searchAmountFrom,
    searchAmountTo,
    betTypeFilter,
  ]);

  const totalViewMoreAmount = useMemo(
    () => viewMoreList.reduce((sum, bet) => sum + Number(parseNumber({ value: bet.bet_amount, type: "float" })), 0),
    [viewMoreList]
  );

  useEffect(() => {
    if (!viewMoreOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [viewMoreOpen]);

  const viewMoreModal = (
    <div style={{ position: "absolute", zIndex: 1040 }}>
      <div className="modal fade show" role="dialog" aria-modal="true" style={{ display: "block" }}>
        <div className="modal-dialog modal-big">
          <span tabIndex={0}></span>
          <div className="modal-content" tabIndex={-1}>
            <header className="modal-header">
              <h5 className="modal-title">View More</h5>
              <button type="button" aria-label="Close" className="close" onClick={() => setViewMoreOpen(false)}>
                ×
              </button>
            </header>
            <div className="modal-body">
              <ul role="tablist" className="nav nav-tabs d-inline-block text-uppercase">
                {marketTabs.map((tab) => (
                  <li className="nav-item d-inline-block" key={tab}>
                    <a
                      data-toggle="tab"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setViewMoreMarketTab(tab);
                      }}
                      className={`nav-link ${viewMoreMarketTab === tab ? "active" : ""}`}
                    >
                      {tab}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="tab-content m-t-20">
                <div className="tabs">
                  <div className="card-header">
                    <ul role="tablist" className="nav nav-pills card-header-pills">
                      <li role="presentation" className="nav-item">
                        <a
                          role="tab"
                          aria-selected={viewMoreTab === "matched"}
                          href="#"
                          className={`nav-link ${viewMoreTab === "matched" ? "active" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setViewMoreTab("matched");
                          }}
                        >
                          Matched Bets
                        </a>
                      </li>
                      <li role="presentation" className="nav-item">
                        <a
                          role="tab"
                          aria-selected={viewMoreTab === "deleted"}
                          href="#"
                          className={`nav-link ${viewMoreTab === "deleted" ? "active" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setViewMoreTab("deleted");
                          }}
                        >
                          Deleted Bets
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="card-body">
                    <form
                      method="post"
                      className="ajaxFormSubmit"
                      onSubmit={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <div className="row row5 align-items-center mb-3">
                        <div className="col-xl-2">
                          <label htmlFor="vm-uname">Username</label>
                          <input
                            id="vm-uname"
                            type="text"
                            placeholder="Search Username"
                            className="form-control"
                            value={searchUserName}
                            onChange={(e) => setSearchUserName(e.target.value)}
                          />
                        </div>
                        <div className="col-xl-3">
                          <div className="row row5">
                            <div className="col-6">
                              <label htmlFor="vm-amountfrom">Amount From</label>
                              <input
                                id="vm-amountfrom"
                                name="amountFrom"
                                type="text"
                                placeholder="Amount From"
                                className="form-control"
                                value={searchAmountFrom}
                                onChange={(e) => setSearchAmountFrom(e.target.value)}
                              />
                            </div>
                            <div className="col-6">
                              <label htmlFor="vm-amountto">Amount To</label>
                              <input
                                id="vm-amountto"
                                name="amountto"
                                type="text"
                                placeholder="Amount To"
                                className="form-control"
                                value={searchAmountTo}
                                onChange={(e) => setSearchAmountTo(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-2">
                          <label htmlFor="vm-ipaddr">IP Address</label>
                          <input
                            id="vm-ipaddr"
                            name="ip"
                            type="text"
                            placeholder="IP Address"
                            className="form-control"
                            value={searchIp}
                            onChange={(e) => setSearchIp(e.target.value)}
                          />
                        </div>
                        <div className="col-xl-2 mt-4">
                          <button type="submit" className="btn btn-primary">
                            Search
                          </button>{" "}
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={() => {
                              setSearchUserName("");
                              setSearchAmountFrom("");
                              setSearchAmountTo("");
                              setSearchIp("");
                              setBetTypeFilter("all");
                            }}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </form>
                    <div className="modal-chekbox">
                      <div role="radiogroup" tabIndex={-1} className="float-left bv-no-focus-ring">
                        <div className="custom-control custom-control-inline custom-radio">
                          <input
                            id="vm-radio-all"
                            type="radio"
                            name="radio-options"
                            className="custom-control-input"
                            value="all"
                            checked={betTypeFilter === "all"}
                            onChange={() => setBetTypeFilter("all")}
                          />
                          <label htmlFor="vm-radio-all" className="custom-control-label">
                            <span>All</span>
                          </label>
                        </div>
                        <div className="custom-control custom-control-inline custom-radio">
                          <input
                            id="vm-radio-back"
                            type="radio"
                            name="radio-options"
                            className="custom-control-input"
                            value="back"
                            checked={betTypeFilter === "back"}
                            onChange={() => setBetTypeFilter("back")}
                          />
                          <label htmlFor="vm-radio-back" className="custom-control-label">
                            <span>Back</span>
                          </label>
                        </div>
                        <div className="custom-control custom-control-inline custom-radio">
                          <input
                            id="vm-radio-lay"
                            type="radio"
                            name="radio-options"
                            className="custom-control-input"
                            value="lay"
                            checked={betTypeFilter === "lay"}
                            onChange={() => setBetTypeFilter("lay")}
                          />
                          <label htmlFor="vm-radio-lay" className="custom-control-label">
                            <span>Lay</span>
                          </label>
                        </div>
                        <div className="custom-control custom-control-inline custom-radio">
                          <input
                            id="vm-radio-deleted"
                            type="radio"
                            name="radio-options"
                            className="custom-control-input"
                            value="deleted"
                            checked={betTypeFilter === "deleted"}
                            onChange={() => setBetTypeFilter("deleted")}
                          />
                          <label htmlFor="vm-radio-deleted" className="custom-control-label">
                            <span>Deleted</span>
                          </label>
                        </div>
                      </div>
                      <div className="custom-control-inline float-right">
                        <h5>
                          Total Soda: <span className="text-success mr-2">{viewMoreList.length}</span> Total Amount:{" "}
                          <span className="text-success">{totalViewMoreAmount.toFixed(2)}</span>
                        </h5>
                      </div>
                    </div>
                    <div className="table-responsive report-table">
                      <table className="table">
                        <thead>
                          <tr>
                            <th className="user-name">
                              <div>Username</div>
                            </th>
                            <th className="event-name">
                              <div>Nation</div>
                            </th>
                            <th className="text-right bet-user-rate">
                              <div>Rate</div>
                            </th>
                            <th className="text-right bet-amount">
                              <div>Amount</div>
                            </th>
                            <th className="bet-date">
                              <div>Date</div>
                            </th>
                            <th>
                              <div>IP</div>
                            </th>
                            <th>
                              <div>B Details</div>
                            </th>
                            <th className="text-right">
                              <div>Action</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {!viewMoreList.length ? (
                            <tr>
                              <td colSpan={8} className="text-center">
                                No records found
                              </td>
                            </tr>
                          ) : (
                            viewMoreList.map((bet, idx) => (
                              <tr className={getRowClass(bet)} key={`${bet.bet_id || idx}-view`}>
                                <td className="user-name">
                                  <div>{bet.user_name || "-"}</div>
                                </td>
                                <td className="event-name">
                                  <div>{getNation(bet)}</div>
                                </td>
                                <td className="text-right bet-user-rate">
                                  <div>{getRate(bet)}</div>
                                </td>
                                <td className="text-right bet-amount">
                                  <div>{getAmount(bet)}</div>
                                </td>
                                <td className="bet-date">
                                  <div>{formatUnix({ unix: getBetDate(bet), format: "DD/MM/YYYY HH:mm:ss" })}</div>
                                </td>
                                <td>
                                  <a href="#" onClick={(e) => e.preventDefault()}>
                                    {bet.ip_address || "-"}
                                  </a>
                                </td>
                                <td>
                                  <a href="#" onClick={(e) => e.preventDefault()} className="text-success">
                                    Detail
                                  </a>
                                </td>
                                <td className="text-right">
                                  <div className="custom-control custom-checkbox">
                                    <input
                                      type="checkbox"
                                      className="custom-control-input"
                                      value="0"
                                      id={`bet-vm-${idx}`}
                                      aria-label={`Select bet row ${idx + 1}`}
                                    />
                                    <label className="custom-control-label" htmlFor={`bet-vm-${idx}`}>
                                      <span className="sr-only">Select bet row {idx + 1}</span>
                                    </label>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <span tabIndex={0}></span>
        </div>
      </div>
      <div className="modal-backdrop"></div>
    </div>
  );

  return (
    <Fragment>
      <div data-simplebar="init" className="right-sidebar">
        <div className="simplebar-wrapper" style={{ margin: "0px" }}>
          <div className="simplebar-height-auto-observer-wrapper">
            <div className="simplebar-height-auto-observer"></div>
          </div>
          <div className="simplebar-mask">
            <div className="simplebar-offset" style={{ right: "0px", bottom: "0px" }}>
              <div
                className="simplebar-content-wrapper"
                tabIndex={0}
                role="region"
                aria-label="scrollable content"
                style={{ height: "auto", overflow: "hidden" }}
              >
                <div className="simplebar-content" style={{ padding: "0px" }}>
                  <div className="card mb-2">
                    <div className="booksum-box-container mb-2">
                      <div data-toggle="collapse" data-target="#demo" className="booksum-title">
                        Book Summary
                      </div>
                      <div id="demo" className="booksum-desc collapse">
                        <div className="booksum-box">
                          <span>No Data Found</span>
                        </div>
                      </div>
                    </div>
                    <div
                      data-toggle="collapse"
                      data-target=".video-tv"
                      aria-expanded="true"
                      className="card-header pointer"
                    >
                      <h6 className="card-title">
                        <a href="#" onClick={(e) => e.preventDefault()} title="">
                          <img
                            src={`${process.env.NODE_ENV === "development" ? "" : BASE_PATH}/arrow-down.svg`}
                            className="mr-1"
                            alt="V"
                          />
                        </a>
                        Live Match
                      </h6>
                    </div>
                    <div className="video-tv collapse"></div>
                  </div>
                  <div className="mb-2">
                    <div className="card-header">
                      <h6 className="card-title float-left">Score Card</h6>
                    </div>
                    {score && event.is_score_available && <ScoreBoard score={score} event={event} />}
                  </div>
                  <div id="my-game-bets" className="card mb-2 my-bet">
                    <div className="card-header">
                      <h6 className="card-title float-left">My Bets</h6>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setViewMoreOpen(true);
                          setViewMoreTab("matched");
                          setBetTypeFilter("all");
                          setSearchUserName("");
                          setSearchAmountFrom("");
                          setSearchAmountTo("");
                          setSearchIp("");
                        }}
                        className="btn btn-back float-right"
                      >
                        View More
                      </a>
                    </div>
                    <div className="card-body">
                      <div className="tabs" id="__BVID__117">
                        <div className="">
                          <ul role="tablist" className="nav nav-tabs small" id="__BVID__117__BV_tab_controls_">
                            <li role="presentation" className="nav-item">
                              <a
                                role="tab"
                                aria-selected={activeBetTab === "matched"}
                                aria-setsize={2}
                                aria-posinset={1}
                                href="#"
                                target="_self"
                                className={`nav-link ${activeBetTab === "matched" ? "active" : ""}`}
                                id="__BVID__118___BV_tab_button__"
                                aria-controls="__BVID__118"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActiveBetTab("matched");
                                }}
                              >
                                Matched Bets
                              </a>
                            </li>
                            <li role="presentation" className="nav-item">
                              <a
                                role="tab"
                                tabIndex={activeBetTab === "unmatched" ? 0 : -1}
                                aria-selected={activeBetTab === "unmatched"}
                                aria-setsize={2}
                                aria-posinset={2}
                                href="#"
                                target="_self"
                                className={`nav-link ${activeBetTab === "unmatched" ? "active" : ""}`}
                                id="__BVID__120___BV_tab_button__"
                                aria-controls="__BVID__120"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActiveBetTab("unmatched");
                                }}
                              >
                                Unmatched Bets
                              </a>
                            </li>
                          </ul>
                        </div>
                        <div className="tab-content" id="__BVID__117__BV_tab_container_">
                          <div
                            role="tabpanel"
                            aria-hidden="false"
                            className={`tab-pane ${activeBetTab === "matched" ? "active" : ""}`}
                            id="__BVID__118"
                            aria-labelledby="__BVID__118___BV_tab_button__"
                            style={activeBetTab === "matched" ? {} : { display: "none" }}
                          >
                            <div id="matched-bet" className="tab-pane active">
                              <div className="table-responsive">
                                <table className="table coupon-table mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ minWidth: "90px" }}>UserName</th>
                                      <th style={{ minWidth: "90px" }}>Nation</th>
                                      <th className="text-right" style={{ minWidth: "50px" }}>
                                        Rate
                                      </th>
                                      <th className="text-right" style={{ minWidth: "70px" }}>
                                        Amount
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!matchedBets.length ? (
                                      <tr>
                                        <td colSpan={4} className="text-center">
                                          No records found
                                        </td>
                                      </tr>
                                    ) : (
                                      matchedBets.map((bet, idx) => (
                                        <Fragment key={`${bet.bet_id || idx}-matched`}>
                                          <tr className={getRowClass(bet)}>
                                            <td colSpan={4}>
                                              <b>{bet.market_name || bet.game_name || ""}</b>{" "}
                                              <span className="float-right">
                                                {formatUnix({ unix: getBetDate(bet), format: "DD/MM/YYYY HH:mm:ss" })}
                                              </span>
                                            </td>
                                          </tr>
                                          <tr className={getRowClass(bet)}>
                                            <td className="bt0">{bet.user_name || "-"}</td>
                                            <td className="bt0">{getNation(bet)}</td>
                                            <td className="text-right bt0">{getRate(bet)}</td>
                                            <td className="text-right bt0">{getAmount(bet)}</td>
                                          </tr>
                                          <tr>
                                            <td colSpan={4} style={{ height: "3px", padding: "0px" }}></td>
                                          </tr>
                                        </Fragment>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          <div
                            role="tabpanel"
                            aria-hidden="true"
                            className={`tab-pane ${activeBetTab === "unmatched" ? "active" : ""}`}
                            id="__BVID__120"
                            aria-labelledby="__BVID__120___BV_tab_button__"
                            style={activeBetTab === "unmatched" ? {} : { display: "none" }}
                          >
                            <div id="unmatched-bet" className="tab-pane">
                              <div className="table-responsive">
                                <table className="table coupon-table mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ minWidth: "90px" }}>UserName</th>
                                      <th style={{ minWidth: "90px" }}>Nation</th>
                                      <th className="text-right" style={{ minWidth: "50px" }}>
                                        Rate
                                      </th>
                                      <th className="text-right" style={{ minWidth: "70px" }}>
                                        Amount
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!unmatchedBets.length ? (
                                      <tr>
                                        <td colSpan={4} className="text-center">
                                          No records found
                                        </td>
                                      </tr>
                                    ) : (
                                      unmatchedBets.map((bet, idx) => (
                                        <tr className={getRowClass(bet)} key={`${bet.bet_id || idx}-unmatched`}>
                                          <td>{bet.user_name || "-"}</td>
                                          <td>{getNation(bet)}</td>
                                          <td className="text-right">{getRate(bet)}</td>
                                          <td className="text-right">{getAmount(bet)}</td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="simplebar-placeholder" style={{ width: "auto", height: "366px" }}></div>
        </div>
        <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
          <div className="simplebar-scrollbar" style={{ width: "0px", display: "none" }}></div>
        </div>
        <div className="simplebar-track simplebar-vertical" style={{ visibility: "hidden" }}>
          <div className="simplebar-scrollbar" style={{ height: "0px", display: "none" }}></div>
        </div>
      </div>
      {viewMoreOpen && createPortal(viewMoreModal, document.body)}
    </Fragment>
  );
};

export default AnalysisRightBar;
